import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { AppService } from '../../app.service';
import { ModalDialogueService } from '../../shared/components/modal-dialogue/modal-dialogue.service';
import { ScreenshotGuideComponent } from '../../shared/components/screenshot-guide/screenshot-guide.component';
import { DataCheckService } from '../../shared/services/data-check.service';
import { DeviceInfoService } from '../../shared/services/device-info.service';
import { LoadingSpinnerService } from '../../shared/services/loading-spinner.service';
import { ToasterService } from '../../shared/services/toaster.service';
import { AuthService } from '../auth.service';

@Component({
  selector: 'gtapp-login',
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: false,
})
export class LoginComponent implements OnInit, AfterViewInit, OnDestroy {
  currentTheme: string = 'default';

  loginEmail: string = '';

  displayMessage: string = '';

  userLinkedSubscribers: any = [];
  selectSubscriberToken: any;

  currentAppVersion: string = '';

  isSuperUser: boolean = false;

  minSupportVersionDevice: any;

  isPWAInstalled: boolean = true;
  deferredPWAInstallPrompt: any;

  // swUpdate: any;

  constructor(
    public router: Router,
    private spinnerService: LoadingSpinnerService,
    private appService: AppService,
    private route: ActivatedRoute,
    private authService: AuthService,
    private dataCheckService: DataCheckService,
    private deviceInfoService: DeviceInfoService,
    private toasterService: ToasterService,
    private dialogService: ModalDialogueService,
  ) {
    const queryParams: any = this.route.snapshot.queryParams || {};

    const inviteeEmail = queryParams?.invitee_email;

    if (
      queryParams?.ms == 1 &&
      this.route.snapshot.url?.[0]?.path == 'webapp'
    ) {
      // should consider links that wont have invitee email in it
      sessionStorage.setItem(btoa('manageSubscriptionRoutedLink'), 'true');
    }
    if (inviteeEmail) {
      this.loginEmail = inviteeEmail;
      localStorage.setItem('email', inviteeEmail);
      this.router.navigate(['/login']);
    }

    this.checkPWAInstallable();
    // when user logins using fido; it opens a seperate window via the browser,
    //whcih we are not able to control as of yet.So if any errors occur on the FIDO;
    //the screen gets stuck on that screen and not our app. This is a cheeky way to bypass that issue.
    // we will store the response data or error message in sessionStorage
    //and just reloads the page forcefully taking the screen of the FIDO intermediate screen
    //and use the response stored and show it in our app page

    let subscribers = JSON.parse(sessionStorage.getItem('response') || '{}');

    if (subscribers?.data?.length) {
      this.getMultipleSubscriber(subscribers);
      setTimeout(() => {
        this.clearLocalStorage();
      }, 1000);
    } else {
      // check whether there is email present in the localstorage beacuse the user had clicked remember me
      let savedEmail = localStorage.getItem('email');

      if (savedEmail) {
        this.spinnerService.show();
        this.loginEmail = savedEmail;
      }
    }
    let errorMessage = sessionStorage.getItem('errorMessage');
    if (errorMessage) {
      this.displayMessage = errorMessage;
      setTimeout(() => {
        this.clearLocalStorage();
      }, 1000);
    }

    const unblockMessage = localStorage.getItem('unblock_account');
    if (unblockMessage) {
      this.displayMessage = unblockMessage;
      setTimeout(() => {
        this.clearLocalStorage();
      }, 1000);
    }
    this.appService
      .getMinVersionCheckDevice()
      .then((value: any) => (this.minSupportVersionDevice = value));
  }
  clearLocalStorage() {
    sessionStorage.clear();
    localStorage.removeItem('unblock_account');
  }
  getMultipleSubscriber(response: any) {
    this.displayMessage = '';
    this.userLinkedSubscribers = response['data'];
    if (this.userLinkedSubscribers?.length) {
      localStorage.setItem('overRidePassword', 'true');
    }
    this.selectSubscriberToken = response['token'];
  }

  async ngOnInit(): Promise<void> {
    this.appService.checkServerStatus().subscribe((response: any) => {
      // save server region on localStorage for changing ui params. Used from global.varaible.ts
      localStorage.setItem(
        btoa('INSTANCE_REGION_NAME'),
        response?.INSTANCE_REGION_NAME || 'AUSTRALIA',
      );
    });
    document.documentElement.setAttribute('data-bs-theme', 'light');
    this.isSuperUser = this.dataCheckService.isSuperUser();

    localStorage.removeItem('resetPasswordPWA');

    this.spinnerService.show();

    const swUpdate: any = this.appService.getSWUpdate();

    if (swUpdate?.isEnabled) {
      try {
        if (await swUpdate?.checkForUpdate()) {
          const updateApp = await swUpdate?.activateUpdate().then(() => {
            sessionStorage.removeItem('updateAvailable');
            window.location.reload();
          });
        } else {
          this.appService.appVersionCheck();
        }
      } catch (error) {
        throw error;
      }
    }

    setTimeout(() => {
      if (sessionStorage.getItem('updateAvailable') === 'true') {
        sessionStorage.removeItem('updateAvailable');
        window.location.reload();
      }
    }, 2000);
    this.spinnerService.hide();
  }
  ngOnDestroy(): void {
    window.removeEventListener(
      'beforeinstallprompt',
      this.beforeInstallPromptHandler,
    );
  }

  ngAfterViewInit(): void {
    this.currentTheme = this.appService.getGTAppTheme();
    if (this.currentTheme !== 'default') {
      //https://app.shortcut.com/sendem/story/10710/add-allow-location-services-access-for-mobile-splash-page
      this.appService.switchGTAppTheme(this.currentTheme);
    }

    this.appService.initializeBootstrapTooltips();

    this.appService
      .appVersionCheck()
      .then((version: any) => (this.currentAppVersion = version?.version));
  }
  selectSubscriber(subscriber: any) {
    this.spinnerService.show();
    localStorage.removeItem('overRidePassword');
    if (this.selectSubscriberToken) {
      this.authService
        .selectSubscriber(this.selectSubscriberToken, subscriber.id, 1)
        .subscribe((response: any) => {
          if (response['status'] == 'success') {
            this.appService.setUserData(response);
            this.loginSuccessAction(response);
          } else {
            this.displayMessage = response['message'];
            this.spinnerService.hide();
          }
        });
    } else if (subscriber?.login_access_data) {
      this.appService.setUserData(subscriber?.login_access_data);
      this.loginSuccessAction(subscriber?.login_access_data);
    } else {
      window.location.reload();
    }
  }

  async loginSuccessAction(response: any) {
    const manageSubscriptionRoutedLink: boolean =
      sessionStorage.getItem(btoa('manageSubscriptionRoutedLink')) == 'true';

    sessionStorage.clear();
    if (manageSubscriptionRoutedLink) {
      sessionStorage.setItem(btoa('manageSubscriptionRoutedLink'), 'true');
    }

    localStorage.removeItem('unblock_account');
    delete response['message'];
    let userData = this.authService.getUserData();
    if (!userData) userData = response;
    userData['status'] = 'verified';
    this.authService.setUserData(userData);
    const swUpdate: any = this.appService.getSWUpdate();

    if (swUpdate?.isEnabled) {
      try {
        this.spinnerService.show('Updating to Latest Version');
        if (await swUpdate?.checkForUpdate()) {
          const updateApp = await swUpdate?.activateUpdate().then(() => {
            sessionStorage.removeItem('updateAvailable');
          });
        }
      } catch (error) {
        throw error;
      }
    }

    this.spinnerService.hide();

    if (this.dataCheckService.isSuperUser() === true) {
      this.router.navigate(['/gtadmin/dashboard']);
    } else if (this.dataCheckService.isPrimeContractorUser()) {
      this.router.navigate(['/pc']);
    } else {
      localStorage.setItem('loginSuccess', 'true');
      this.router.navigate(['./dashboard']);
    }
  }

  beforeInstallPromptHandler = (event: any) => {
    event.preventDefault();
    // Stash the event so it can be triggered later
    this.deferredPWAInstallPrompt = event;
    this.isPWAInstalled = false;
    this.deviceInfoService.deviceInfoSubject.next({
      deferredPWAInstallPrompt: event,
    });
  };
  async checkPWAInstallable() {
    // return if there is no service worker
    const swUpdate: any = this.appService.getSWUpdate();

    if (!swUpdate?.isEnabled) return;
    // return if its a pwa
    const isDevicePWA = this.deviceInfoService.isDevicePWA();
    if (isDevicePWA) return;

    window.addEventListener(
      'beforeinstallprompt',
      this.beforeInstallPromptHandler,
    );

    const isIOS = this.deviceInfoService.isPhoneIOS();
    if (isIOS) {
      // for ios `beforeinstallprompt` is not triggered, so if its not a pwa we will show it always
      this.deferredPWAInstallPrompt = true;
      this.isPWAInstalled = false;
    }
  }

  installPWAApp() {
    this.spinnerService.show();
    const isIOS = this.deviceInfoService.isPhoneIOS();
    if (isIOS) {
      this.dialogService.open(ScreenshotGuideComponent);

      this.spinnerService.hide();
    } else if (this.deferredPWAInstallPrompt) {
      this.deferredPWAInstallPrompt.prompt();
      this.spinnerService.hide();
      // Wait for the user to respond to the prompt
      this.deferredPWAInstallPrompt.userChoice.then((choiceResult: any) => {
        if (choiceResult.outcome === 'accepted') {
          // Hide the install button after installation
          this.isPWAInstalled = true;
        }
        // Clear the deferredPrompt variable
        this.deferredPWAInstallPrompt = null;
        this.deviceInfoService.deviceInfoSubject.next({
          deferredPWAInstallPrompt: null,
        });
      });
    } else {
      this.spinnerService.hide();
      this.toasterService.setMessage({
        successMessage: '',
        errorMessage: 'ERROR: Guard Tracker may already be installed',
      });
    }
  }
  emitLoggedInData(eventData: any) {
    switch (eventData?.source) {
      case 'multipleSubscribers':
        this.getMultipleSubscriber(eventData?.response);
        break;
      case 'loginSuccessAction':
        this.appService.setUserData(eventData?.response);
        this.loginSuccessAction(eventData?.response);
        break;
      case 'displayMessage':
        this.displayMessage = eventData?.response?.message;
        break;
    }
  }
}
